import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { set } from 'lodash-es';
import { flushSync } from 'react-dom';
import { useNotifications } from 'hooks/useNotifications';
import {
  BMSVoucherGuest,
  VoucherTransfer,
  VoucherGroundService,
  VoucherExperience,
  VoucherHotel,
  ProcessedUpload,
} from 'services/ItineraryManagerApi/types';
import { useVoucherEditQueries } from '../useVoucherEditQueries';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';

interface IVoucherEditState {
  guests: BMSVoucherGuest[];
  guestsMoreInfo: { text: string; isVisible: boolean };
  peBookingNumber: { text: string; isVisible: boolean };
  travelPartnerReference: { text: string; isVisible: boolean };
  destinationDetailsIsVisible: boolean;
  specialRequest: { text: string; isVisible: boolean };
  specialRequestMoreInfo: { text: string; isVisible: boolean };
  extraServices: { text: string; isVisible: boolean };
  additionalInformation: { text: string; isVisible: boolean };
  membershipBenefits: { text: string; isVisible: boolean };
  policiesAndRestrictions: { text: string; isVisible: boolean };
  thingsToBeAwareOf: { text: string; isVisible: boolean };
  emergencyContact: string;
  transfers: VoucherTransfer[];
  groundServices: VoucherGroundService[];
  experiences: VoucherExperience[];
  hotels: VoucherHotel[];
  voucherImage?: ProcessedUpload | null;
}

interface IUseVoucherEditProps {
  voucherData: any;
  voucherUuid: string;
}

export const useVoucherEdit = ({ voucherData, voucherUuid }: IUseVoucherEditProps) => {
  const history = useHistory();
  const notifications = useNotifications();

  const [isEditedWithoutSaving, setIsEditedWithoutSaving] = useState(false);
  const [voucherEditState, setVoucherEditState] = useState<IVoucherEditState>({
    guests: [],
    guestsMoreInfo: { text: '', isVisible: false },
    peBookingNumber: { text: '', isVisible: false },
    travelPartnerReference: { text: '', isVisible: false },
    destinationDetailsIsVisible: false,
    specialRequest: { text: '', isVisible: false },
    specialRequestMoreInfo: { text: '', isVisible: false },
    extraServices: { text: '', isVisible: false },
    additionalInformation: { text: '', isVisible: false },
    membershipBenefits: { text: '', isVisible: false },
    policiesAndRestrictions: { text: '', isVisible: false },
    thingsToBeAwareOf: { text: '', isVisible: false },
    emergencyContact: '',
    transfers: [],
    groundServices: [],
    experiences: [],
    hotels: [],
    voucherImage: undefined,
  });

  const voucherEditQueries = useVoucherEditQueries(makeItineraryManagerApi(), voucherUuid);

  // Generic update function using path-based updates
  const updateVoucherData = useCallback((path: string, value: any) => {
    setVoucherEditState(prev => {
      const newState = { ...prev };
      set(newState, path, value);
      return newState;
    });
    setIsEditedWithoutSaving(true);
  }, []);

  // Helper function for updating array items by UUID
  const updateArrayItem = useCallback((arrayPath: string, uuid: string, updates: any) => {
    setVoucherEditState(prev => {
      const newState = { ...prev };
      const array = newState[arrayPath as keyof IVoucherEditState] as any[];
      const updatedArray = array.map(item => (item.uuid === uuid ? { ...item, ...updates } : item));
      set(newState, arrayPath, updatedArray);
      return newState;
    });
    setIsEditedWithoutSaving(true);
  }, []);

  // Helper function for updating nested array items (e.g., hotel accommodations)
  const updateNestedArrayItem = useCallback(
    (basePath: string, baseUuid: string, nestedPath: string, nestedUuid: string, updates: any) => {
      setVoucherEditState(prev => {
        const newState = { ...prev };
        const baseArray = newState[basePath as keyof IVoucherEditState] as any[];
        const updatedBaseArray = baseArray.map(baseItem => {
          if (baseItem.uuid === baseUuid) {
            const nestedArray = baseItem[nestedPath];
            const updatedNestedArray = nestedArray.map((nestedItem: any) =>
              nestedItem.uuid === nestedUuid ? { ...nestedItem, ...updates } : nestedItem
            );
            return { ...baseItem, [nestedPath]: updatedNestedArray };
          }
          return baseItem;
        });
        set(newState, basePath, updatedBaseArray);
        return newState;
      });
      setIsEditedWithoutSaving(true);
    },
    []
  );

  // Helper function for updating triple-nested array items (e.g., hotel > accommodation > mealPlan)
  const updateTripleNestedArrayItem = useCallback(
    (
      basePath: string,
      baseUuid: string,
      nestedPath: string,
      nestedUuid: string,
      deepPath: string,
      deepUuid: string,
      updates: any
    ) => {
      setVoucherEditState(prev => {
        const newState = { ...prev };
        const baseArray = newState[basePath as keyof IVoucherEditState] as any[];
        const updatedBaseArray = baseArray.map(baseItem => {
          if (baseItem.uuid === baseUuid) {
            const nestedArray = baseItem[nestedPath];
            const updatedNestedArray = nestedArray.map((nestedItem: any) => {
              if (nestedItem.uuid === nestedUuid) {
                const deepArray = nestedItem[deepPath];
                const updatedDeepArray = deepArray.map((deepItem: any) =>
                  deepItem.uuid === deepUuid ? { ...deepItem, ...updates } : deepItem
                );
                return { ...nestedItem, [deepPath]: updatedDeepArray };
              }
              return nestedItem;
            });
            return { ...baseItem, [nestedPath]: updatedNestedArray };
          }
          return baseItem;
        });
        set(newState, basePath, updatedBaseArray);
        return newState;
      });
      setIsEditedWithoutSaving(true);
    },
    []
  );

  // Helper function for updating accommodation properties (like supplementsMoreInfo)
  const updateAccommodationProperty = useCallback(
    (hotelUuid: string, accommodationUuid: string, property: string, value: any) => {
      setVoucherEditState(prev => {
        const newState = { ...prev };
        const hotels = newState.hotels.map(hotel => {
          if (hotel.uuid === hotelUuid) {
            const accommodations = hotel.accommodations.map(accommodation => {
              if (accommodation.uuid === accommodationUuid) {
                return { ...accommodation, [property]: value };
              }
              return accommodation;
            });
            return { ...hotel, accommodations };
          }
          return hotel;
        });
        set(newState, 'hotels', hotels);
        return newState;
      });
      setIsEditedWithoutSaving(true);
    },
    []
  );

  // Initialize local state with voucher data
  useEffect(() => {
    if (voucherData?.voucher) {
      setVoucherEditState({
        guests: voucherData.voucher.guests || [],
        guestsMoreInfo: {
          text: voucherData.voucher.guestsMoreInfo?.text || '',
          isVisible: voucherData.voucher.guestsMoreInfo?.isVisible || false,
        },
        peBookingNumber: {
          text: voucherData.voucher.peBookingNumber?.text || '',
          isVisible: voucherData.voucher.peBookingNumber?.isVisible || false,
        },
        travelPartnerReference: {
          text: voucherData.voucher.travelPartnerReference?.text || '',
          isVisible: voucherData.voucher.travelPartnerReference?.isVisible || false,
        },
        destinationDetailsIsVisible: voucherData.voucher.destinationDetailsIsVisible || false,
        specialRequest: {
          text: voucherData.voucher.specialRequest?.text || '',
          isVisible: voucherData.voucher.specialRequest?.isVisible || false,
        },
        specialRequestMoreInfo: {
          text: voucherData.voucher.specialRequestMoreInfo?.text || '',
          isVisible: voucherData.voucher.specialRequestMoreInfo?.isVisible || false,
        },
        extraServices: {
          text: voucherData.voucher.extraServices?.text || '',
          isVisible: voucherData.voucher.extraServices?.isVisible || false,
        },
        additionalInformation: {
          text: voucherData.voucher.additionalInformation?.text || '',
          isVisible: voucherData.voucher.additionalInformation?.isVisible || false,
        },
        membershipBenefits: {
          text: voucherData.voucher.membershipBenefits?.text || '',
          isVisible: voucherData.voucher.membershipBenefits?.isVisible || false,
        },
        policiesAndRestrictions: {
          text: voucherData.voucher.policiesAndRestrictions?.text || '',
          isVisible: voucherData.voucher.policiesAndRestrictions?.isVisible || false,
        },
        thingsToBeAwareOf: {
          text: voucherData.voucher.thingsToBeAwareOf?.text || '',
          isVisible: voucherData.voucher.thingsToBeAwareOf?.isVisible || false,
        },
        emergencyContact: voucherData.voucher.emergencyContact || '',
        transfers: voucherData.voucher.transfers || [],
        groundServices: voucherData.voucher.groundServices || [],
        experiences: voucherData.voucher.experiences || [],
        hotels: voucherData.voucher.hotels || [],
        voucherImage: voucherData.voucher.voucherImage || undefined,
      });
    }
  }, [voucherData]);

  // Handle guest visibility toggle
  const handleGuestVisibilityToggle = useCallback(
    (guestUuid: string, isVisible: boolean) => {
      updateArrayItem('guests', guestUuid, { isVisible });
    },
    [updateArrayItem]
  );

  // Handle transfer updates
  const handleTransferVisibilityToggle = useCallback(
    (transferUuid: string, isVisible: boolean) => {
      updateArrayItem('transfers', transferUuid, { isVisible });
    },
    [updateArrayItem]
  );

  const handleTransferDescriptionChange = useCallback(
    (transferUuid: string, description: string) => {
      updateArrayItem('transfers', transferUuid, { description });
    },
    [updateArrayItem]
  );

  const handleTransferMoreTextChange = useCallback(
    (transferUuid: string, moreText: string) => {
      updateArrayItem('transfers', transferUuid, { moreText });
    },
    [updateArrayItem]
  );

  const handleTransferAddUpload = useCallback((transferUuid: string, uploadData: any) => {
    setVoucherEditState(prev => {
      const newState = { ...prev };
      const transfers = newState.transfers.map(transfer =>
        transfer.uuid === transferUuid ? { ...transfer, uploads: [...transfer.uploads, uploadData] } : transfer
      );
      return { ...newState, transfers };
    });
    setIsEditedWithoutSaving(true);
  }, []);

  const handleTransferRemoveUpload = useCallback((transferUuid: string, uploadIndex: number) => {
    setVoucherEditState(prev => {
      const newState = { ...prev };
      const transfers = newState.transfers.map(transfer =>
        transfer.uuid === transferUuid
          ? {
              ...transfer,
              uploads: transfer.uploads.filter((_, idx) => idx !== uploadIndex),
            }
          : transfer
      );
      return { ...newState, transfers };
    });
    setIsEditedWithoutSaving(true);
  }, []);

  // Handle ground service updates
  const handleGroundServiceVisibilityToggle = useCallback(
    (groundServiceUuid: string, isVisible: boolean) => {
      updateArrayItem('groundServices', groundServiceUuid, { isVisible });
    },
    [updateArrayItem]
  );

  const handleGroundServiceDescriptionChange = useCallback(
    (groundServiceUuid: string, description: string) => {
      updateArrayItem('groundServices', groundServiceUuid, { description });
    },
    [updateArrayItem]
  );

  const handleGroundServiceMoreTextChange = useCallback(
    (groundServiceUuid: string, moreText: string) => {
      updateArrayItem('groundServices', groundServiceUuid, { moreText });
    },
    [updateArrayItem]
  );

  const handleGroundServiceAddUpload = useCallback((groundServiceUuid: string, uploadData: any) => {
    setVoucherEditState(prev => {
      const newState = { ...prev };
      const groundServices = newState.groundServices.map(groundService =>
        groundService.uuid === groundServiceUuid
          ? { ...groundService, uploads: [...groundService.uploads, uploadData] }
          : groundService
      );
      return { ...newState, groundServices };
    });
    setIsEditedWithoutSaving(true);
  }, []);

  const handleGroundServiceRemoveUpload = useCallback((groundServiceUuid: string, uploadIndex: number) => {
    setVoucherEditState(prev => {
      const newState = { ...prev };
      const groundServices = newState.groundServices.map(groundService =>
        groundService.uuid === groundServiceUuid
          ? {
              ...groundService,
              uploads: groundService.uploads.filter((_, idx) => idx !== uploadIndex),
            }
          : groundService
      );
      return { ...newState, groundServices };
    });
    setIsEditedWithoutSaving(true);
  }, []);

  // Handle experience updates
  const handleExperienceVisibilityToggle = useCallback(
    (experienceUuid: string, isVisible: boolean) => {
      updateArrayItem('experiences', experienceUuid, { isVisible });
    },
    [updateArrayItem]
  );

  const handleExperienceDescriptionChange = useCallback(
    (experienceUuid: string, description: string) => {
      updateArrayItem('experiences', experienceUuid, { description });
    },
    [updateArrayItem]
  );

  const handleExperienceMoreTextChange = useCallback(
    (experienceUuid: string, moreText: string) => {
      updateArrayItem('experiences', experienceUuid, { moreText });
    },
    [updateArrayItem]
  );

  const handleExperienceAddUpload = useCallback((experienceUuid: string, uploadData: any) => {
    setVoucherEditState(prev => {
      const newState = { ...prev };
      const experiences = newState.experiences.map(experience =>
        experience.uuid === experienceUuid
          ? { ...experience, uploads: [...experience.uploads, uploadData] }
          : experience
      );
      return { ...newState, experiences };
    });
    setIsEditedWithoutSaving(true);
  }, []);

  const handleExperienceRemoveUpload = useCallback((experienceUuid: string, uploadIndex: number) => {
    setVoucherEditState(prev => {
      const newState = { ...prev };
      const experiences = newState.experiences.map(experience =>
        experience.uuid === experienceUuid
          ? {
              ...experience,
              uploads: experience.uploads.filter((_, idx) => idx !== uploadIndex),
            }
          : experience
      );
      return { ...newState, experiences };
    });
    setIsEditedWithoutSaving(true);
  }, []);

  // Handle hotel visibility updates
  const handleHotelPhoneVisibilityToggle = useCallback(
    (hotelUuid: string, isVisible: boolean) => {
      updateArrayItem('hotels', hotelUuid, { isPhoneVisible: isVisible });
    },
    [updateArrayItem]
  );

  const handleHotelEmailVisibilityToggle = useCallback(
    (hotelUuid: string, isVisible: boolean) => {
      updateArrayItem('hotels', hotelUuid, { isEmailVisible: isVisible });
    },
    [updateArrayItem]
  );

  // Handle hotel accommodation updates
  const handleAccommodationVisibilityToggle = useCallback(
    (hotelUuid: string, accommodationUuid: string, isVisible: boolean) => {
      updateNestedArrayItem('hotels', hotelUuid, 'accommodations', accommodationUuid, { isVisible });
    },
    [updateNestedArrayItem]
  );

  const handleAccommodationDescriptionChange = useCallback(
    (hotelUuid: string, accommodationUuid: string, description: string) => {
      updateNestedArrayItem('hotels', hotelUuid, 'accommodations', accommodationUuid, { description });
    },
    [updateNestedArrayItem]
  );

  const handleAccommodationMoreTextChange = useCallback(
    (hotelUuid: string, accommodationUuid: string, moreText: string) => {
      updateNestedArrayItem('hotels', hotelUuid, 'accommodations', accommodationUuid, { moreText });
    },
    [updateNestedArrayItem]
  );

  const handleMealPlanDescriptionChange = useCallback(
    (hotelUuid: string, accommodationUuid: string, mealPlanUuid: string, description: string) => {
      updateTripleNestedArrayItem('hotels', hotelUuid, 'accommodations', accommodationUuid, 'mealPlans', mealPlanUuid, {
        description,
      });
    },
    [updateTripleNestedArrayItem]
  );

  const handleSupplementVisibilityToggle = useCallback(
    (hotelUuid: string, accommodationUuid: string, supplementUuid: string, isVisible: boolean) => {
      updateTripleNestedArrayItem(
        'hotels',
        hotelUuid,
        'accommodations',
        accommodationUuid,
        'supplements',
        supplementUuid,
        { isVisible }
      );
    },
    [updateTripleNestedArrayItem]
  );

  const handleSupplementTertiaryTextChange = useCallback(
    (hotelUuid: string, accommodationUuid: string, supplementUuid: string, tertiaryText: string) => {
      updateTripleNestedArrayItem(
        'hotels',
        hotelUuid,
        'accommodations',
        accommodationUuid,
        'supplements',
        supplementUuid,
        { tertiaryText }
      );
    },
    [updateTripleNestedArrayItem]
  );

  const handleAdditionalServiceTertiaryTextChange = useCallback(
    (hotelUuid: string, accommodationUuid: string, serviceUuid: string, tertiaryText: string) => {
      updateTripleNestedArrayItem(
        'hotels',
        hotelUuid,
        'accommodations',
        accommodationUuid,
        'additionalHotelServices',
        serviceUuid,
        { tertiaryText }
      );
    },
    [updateTripleNestedArrayItem]
  );

  const handleSupplementsMoreInfoChange = useCallback(
    (hotelUuid: string, accommodationUuid: string, text: string) => {
      updateAccommodationProperty(hotelUuid, accommodationUuid, 'supplementsMoreInfo', {
        text,
        isVisible:
          voucherEditState.hotels
            .find(h => h.uuid === hotelUuid)
            ?.accommodations.find(a => a.uuid === accommodationUuid)?.supplementsMoreInfo?.isVisible || true,
      });
    },
    [updateAccommodationProperty, voucherEditState.hotels]
  );

  const handleAdditionalServicesMoreInfoChange = useCallback(
    (hotelUuid: string, accommodationUuid: string, text: string) => {
      updateAccommodationProperty(hotelUuid, accommodationUuid, 'additionalHotelServicesMoreInfo', {
        text,
        isVisible:
          voucherEditState.hotels
            .find(h => h.uuid === hotelUuid)
            ?.accommodations.find(a => a.uuid === accommodationUuid)?.additionalHotelServicesMoreInfo?.isVisible ||
          true,
      });
    },
    [updateAccommodationProperty, voucherEditState.hotels]
  );

  const handleAdditionalServiceVisibilityToggle = useCallback(
    (hotelUuid: string, accommodationUuid: string, serviceUuid: string, isVisible: boolean) => {
      updateTripleNestedArrayItem(
        'hotels',
        hotelUuid,
        'accommodations',
        accommodationUuid,
        'additionalHotelServices',
        serviceUuid,
        { isVisible }
      );
    },
    [updateTripleNestedArrayItem]
  );

  // Handle rate features visibility toggle
  const handleRateFeaturesVisibilityToggle = useCallback(
    (hotelUuid: string, accommodationUuid: string, isVisible: boolean) => {
      updateNestedArrayItem('hotels', hotelUuid, 'accommodations', accommodationUuid, { isRateFeaturesVisible: isVisible });
    },
    [updateNestedArrayItem]
  );

  // Accommodation upload handlers
  const handleAccommodationAddUpload = useCallback((hotelUuid: string, accommodationUuid: string, uploadData: any) => {
    setVoucherEditState(prev => {
      const newState = { ...prev };
      const hotels = newState.hotels.map(hotel => {
        if (hotel.uuid === hotelUuid) {
          const accommodations = hotel.accommodations.map(accommodation => {
            if (accommodation.uuid === accommodationUuid) {
              return { ...accommodation, uploads: [...accommodation.uploads, uploadData] };
            }
            return accommodation;
          });
          return { ...hotel, accommodations };
        }
        return hotel;
      });
      return { ...newState, hotels };
    });
    setIsEditedWithoutSaving(true);
  }, []);

  const handleAccommodationRemoveUpload = useCallback(
    (hotelUuid: string, accommodationUuid: string, uploadIndex: number) => {
      setVoucherEditState(prev => {
        const newState = { ...prev };
        const hotels = newState.hotels.map(hotel => {
          if (hotel.uuid === hotelUuid) {
            const accommodations = hotel.accommodations.map(accommodation => {
              if (accommodation.uuid === accommodationUuid) {
                return {
                  ...accommodation,
                  uploads: accommodation.uploads.filter((_, idx) => idx !== uploadIndex),
                };
              }
              return accommodation;
            });
            return { ...hotel, accommodations };
          }
          return hotel;
        });
        return { ...newState, hotels };
      });
      setIsEditedWithoutSaving(true);
    },
    []
  );

  // Meal plan upload handlers
  const handleMealPlanAddUpload = useCallback(
    (hotelUuid: string, accommodationUuid: string, mealPlanUuid: string, uploadData: any) => {
      setVoucherEditState(prev => {
        const newState = { ...prev };
        const hotels = newState.hotels.map(hotel => {
          if (hotel.uuid === hotelUuid) {
            const accommodations = hotel.accommodations.map(accommodation => {
              if (accommodation.uuid === accommodationUuid) {
                const mealPlans = accommodation.mealPlans.map(mealPlan => {
                  if (mealPlan.uuid === mealPlanUuid) {
                    return { ...mealPlan, uploads: [...mealPlan.uploads, uploadData] };
                  }
                  return mealPlan;
                });
                return { ...accommodation, mealPlans };
              }
              return accommodation;
            });
            return { ...hotel, accommodations };
          }
          return hotel;
        });
        return { ...newState, hotels };
      });
      setIsEditedWithoutSaving(true);
    },
    []
  );

  const handleMealPlanRemoveUpload = useCallback(
    (hotelUuid: string, accommodationUuid: string, mealPlanUuid: string, uploadIndex: number) => {
      setVoucherEditState(prev => {
        const newState = { ...prev };
        const hotels = newState.hotels.map(hotel => {
          if (hotel.uuid === hotelUuid) {
            const accommodations = hotel.accommodations.map(accommodation => {
              if (accommodation.uuid === accommodationUuid) {
                const mealPlans = accommodation.mealPlans.map(mealPlan => {
                  if (mealPlan.uuid === mealPlanUuid) {
                    return {
                      ...mealPlan,
                      uploads: mealPlan.uploads.filter((_, idx) => idx !== uploadIndex),
                    };
                  }
                  return mealPlan;
                });
                return { ...accommodation, mealPlans };
              }
              return accommodation;
            });
            return { ...hotel, accommodations };
          }
          return hotel;
        });
        return { ...newState, hotels };
      });
      setIsEditedWithoutSaving(true);
    },
    []
  );

  // Handle logo upload
  const handleLogoUpload = useCallback(
    (uploadData: any) => {
      // Handle different cases:
      // - undefined: modal cancelled or upload failed, don't update (preserve existing logo)
      // - null: "No Logo" button clicked, update to null (remove logo)
      // - object: successful upload, update with new logo data
      if (uploadData !== undefined) {
        updateVoucherData('voucherImage', uploadData);
      }
    },
    [updateVoucherData]
  );

  // Handle save
  const handleSave = useCallback(async () => {
    if (!voucherData?.voucher) {
      notifications.showErrorNotification('No voucher data to save');
      return;
    }

    try {
      const updatedVoucherData = {
        ...voucherData.voucher,
        guests: voucherEditState.guests,
        guestsMoreInfo: voucherEditState.guestsMoreInfo,
        peBookingNumber: voucherEditState.peBookingNumber
          ? {
              text: voucherData.voucher.peBookingNumber?.text || '',
              isVisible: voucherEditState.peBookingNumber.isVisible,
            }
          : undefined,
        travelPartnerReference: voucherEditState.travelPartnerReference
          ? {
              text: voucherData.voucher.travelPartnerReference?.text || '',
              isVisible: voucherEditState.travelPartnerReference.isVisible,
            }
          : undefined,
        destinationDetailsIsVisible: voucherEditState.destinationDetailsIsVisible,
        specialRequest: voucherEditState.specialRequest,
        specialRequestMoreInfo: voucherEditState.specialRequestMoreInfo,
        extraServices: voucherEditState.extraServices,
        additionalInformation: voucherEditState.additionalInformation,
        membershipBenefits: voucherEditState.membershipBenefits,
        policiesAndRestrictions: voucherEditState.policiesAndRestrictions,
        thingsToBeAwareOf: voucherEditState.thingsToBeAwareOf,
        emergencyContact: voucherEditState.emergencyContact,
        transfers: voucherEditState.transfers,
        groundServices: voucherEditState.groundServices,
        experiences: voucherEditState.experiences,
        hotels: voucherEditState.hotels,
        voucherImage: voucherEditState.voucherImage !== null ? voucherEditState.voucherImage : undefined,
      };

      await voucherEditQueries.saveVoucher.mutateAsync(updatedVoucherData);
      notifications.showSuccessNotification('Voucher saved successfully');
      flushSync(() => {
        setIsEditedWithoutSaving(false);
      });

      // Redirect to view page after successful save
      history.push(`/voucher/${voucherUuid}`);
    } catch (error) {
      console.error('Failed to save voucher:', error);
      // Error notification is handled in the mutation hook
    }
  }, [voucherData, voucherEditState, voucherEditQueries.saveVoucher, notifications, history, voucherUuid]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    history.push(`/voucher/${voucherUuid}`);
  }, [history, voucherUuid]);

  return {
    voucherEditState,
    isEditedWithoutSaving,
    updateVoucherData,
    handleGuestVisibilityToggle,
    handleTransferVisibilityToggle,
    handleTransferDescriptionChange,
    handleTransferMoreTextChange,
    handleTransferAddUpload,
    handleTransferRemoveUpload,
    handleGroundServiceVisibilityToggle,
    handleGroundServiceDescriptionChange,
    handleGroundServiceMoreTextChange,
    handleGroundServiceAddUpload,
    handleGroundServiceRemoveUpload,
    handleExperienceVisibilityToggle,
    handleExperienceDescriptionChange,
    handleExperienceMoreTextChange,
    handleExperienceAddUpload,
    handleExperienceRemoveUpload,
    handleHotelPhoneVisibilityToggle,
    handleHotelEmailVisibilityToggle,
    handleAccommodationVisibilityToggle,
    handleAccommodationDescriptionChange,
    handleAccommodationMoreTextChange,
    handleMealPlanDescriptionChange,
    handleSupplementVisibilityToggle,
    handleSupplementTertiaryTextChange,
    handleAdditionalServiceVisibilityToggle,
    handleAdditionalServiceTertiaryTextChange,
    handleSupplementsMoreInfoChange,
    handleAdditionalServicesMoreInfoChange,
    handleRateFeaturesVisibilityToggle,
    handleAccommodationAddUpload,
    handleAccommodationRemoveUpload,
    handleMealPlanAddUpload,
    handleMealPlanRemoveUpload,
    handleLogoUpload,
    handleSave,
    handleCancel,
    isSaveLoading: voucherEditQueries.saveVoucher.isPending,
  };
};
